import { FreeSpinButtons } from '@/features/Popup/anexes/FreeSpinButtons';
import { PopupProps } from '@/features/Popup/types';
import '@/features/Popup/styles/index.scss';
import React, { useEffect } from 'react';
import { CUSTOM_SIZES, POPUP_TYPES } from './constants';
import {
  TRACK_USER_ACTION_EVENT,
  getExitModalZone,
  useTrackUserAction,
} from '@/utilities/logger/amplitude/tracking';
import { stopVitalsOnError } from '@/utilities/logger/datadog/vitals';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import { useStore } from '@/store/useStore';

const getContainerTestId = (type?: string): string => {
  switch (type) {
    case POPUP_TYPES.EXIT:
      return 'exit-game-popup-container';
    case POPUP_TYPES.EXIT_RECENTLY:
      return 'recently-played-popup-container';
    case POPUP_TYPES.SWJ:
      return 'swj-error-popup-container';
    default:
      return 'error-popup-container';
  }
};

const getTitleTestId = (type?: string): string => {
  switch (type) {
    case POPUP_TYPES.SWJ:
      return 'swj-error-title';
    default:
      return 'error-popup-title';
  }
};

const getMessageTestId = (type?: string): string => {
  switch (type) {
    case POPUP_TYPES.EXIT:
      return 'exit-game-prompt-message';
    case POPUP_TYPES.EXIT_RECENTLY:
      return 'recently-played-popup-title';
    case POPUP_TYPES.SWJ:
      return 'swj-error-message';
    default:
      return 'error-popup-message';
  }
};

const GenericPopup: React.FC<PopupProps> = ({
  config,
  onClose,
  errorData,
  type,
}) => {
  const { header, main, footer } = config;

  const errorTitle = main?.popupName;
  const errorMessage = main?.popupMessage;
  const horizontalButtons = main?.horizontalButtons;
  const isSmall = main?.customSize === CUSTOM_SIZES.SMALL;

  const resolvedErrorData = errorData ?? config.errorData;
  const errorType = resolvedErrorData?.errorType;
  const errorCode = resolvedErrorData?.errorCode;
  const errorParameters = resolvedErrorData?.errorParameters;

  const { trackUserAction } = useTrackUserAction();
  const isErrorPopup = type === POPUP_TYPES.PROVIDER || Boolean(errorType);
  const isExitPopup =
    type === POPUP_TYPES.EXIT || type === POPUP_TYPES.EXIT_RECENTLY;

  useEffect(() => {
    if (isErrorPopup) {
      // Stop any launch vitals still hanging because the game errored before
      // GameReady (provider error or GLS failure) so Datadog gets a bounded,
      // error-tagged duration instead of an abandoned span.
      const gameLoaded = useStore.getState().gameStatus.gameLoadSuccess;

      if (!gameLoaded) {
        Logger.logRumError({
          name: '[GW][GAME_LAUNCH] Failed',
          useCase: DdEventCategory.GameLaunch,
          eventType: DdEventType.Error,
        });
      }

      stopVitalsOnError({ errorType, errorCode, errorMessage });

      trackUserAction({
        event: TRACK_USER_ACTION_EVENT.IN_GAME_ERROR_MODAL_VIEWED,
        errorType,
        pageName: 'GW',
      });
    }

    if (isExitPopup) {
      trackUserAction({
        event: TRACK_USER_ACTION_EVENT.IN_GAME_EXIT_MODAL_VIEWED,
        zone: getExitModalZone(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`popup__container ${isSmall ? 'popup__container--small' : ''}`}
      data-testid={getContainerTestId(type)}
      data-popup-type={type}
      data-error-type={errorType}
    >
      {(header?.backButton || header?.closeButton) && (
        <div className="popup__header">
          {header?.backButton}
          {header?.closeButton}
        </div>
      )}

      <div className="popup__main">
        {main?.icon}
        {errorTitle && (
          <h3 className="popup__title" data-testid={getTitleTestId(type)}>
            {errorTitle}
          </h3>
        )}
        {errorMessage && (
          <p className="popup__message" data-testid={getMessageTestId(type)}>
            {errorMessage}
          </p>
        )}

        <div
          className={`popup__buttons ${
            horizontalButtons ? 'popup__buttons--horizontal' : ''
          }`}
        >
          <FreeSpinButtons
            errorType={errorType}
            errorParameters={errorParameters}
            onClose={onClose}
          />
          {main?.buttons}
        </div>
      </div>

      {footer && <div className="popup__footer">{footer}</div>}
    </div>
  );
};

export default GenericPopup;
